import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="selected-attachments"
//
// 添付モーダルで選択したファイルを、送信するまでブラウザ内だけで保持する。
// アップロードは行わず、送信時に <input type="file"> ごと本文と一緒にサーバーへ送る。
// モーダルを閉じる（close イベント）と選択中ファイルを破棄する。サーバー通信は発生しない。
export default class extends Controller {
  static targets = ["dialog", "input", "list", "itemTemplate", "errorBanner", "errorTemplate"]
  static values = { maxCount: Number, countMessage: String, typeMessage: String }

  connect() {
    // 送信成功時などで画面が作り直された場合、一覧（data-turbo-permanent）に前回の描画が残るため空から始める
    this.clear()
    this.dialogTarget.addEventListener("close", this.clear)
    this.element.addEventListener("submit", this.syncBeforeSubmit)
  }

  disconnect() {
    this.dialogTarget.removeEventListener("close", this.clear)
    this.element.removeEventListener("submit", this.syncBeforeSubmit)
  }

  // 添付を受け付けない状態か（input の disabled。送信中は message_disabled? のサーバー値がポーリングの morph で反映される）
  get disabled() {
    return this.inputTarget.disabled
  }

  // <input type="file"> のネイティブ選択ダイアログ経由
  inputChanged() {
    this.add(Array.from(this.inputTarget.files ?? []))
  }

  // ドロップ（file-drop コントローラー）・ファイル選択の共通受け口。
  // 拡張子NG・件数超過は弾き、OKな分だけ追加する。重複（名前＋サイズ）は黙って無視する。
  add(files) {
    if (this.disabled) return

    const reasons = new Set()
    const accepted = []

    for (const file of files) {
      if (this.stagedFiles.length + accepted.length >= this.maxCountValue) {
        reasons.add(this.countMessageValue)
        break
      }
      if (!this.isAcceptableType(file)) {
        reasons.add(this.typeMessageValue)
        continue
      }
      if (this.isDuplicate(file, accepted)) continue

      accepted.push(file)
    }

    this.stagedFiles = [...this.stagedFiles, ...accepted]
    // 全件弾いた場合もブラウザ標準の選択で置き換わった input.files を戻すため、常に組み直す
    this.sync()

    // 複数の理由が同時に起きた場合も1行に潰さず区切って見せる
    if (reasons.size > 0) this.showError(Array.from(reasons).join("／"))
    else this.clearError()
  }

  remove({ params: { index } }) {
    this.stagedFiles = this.stagedFiles.filter((_, i) => i !== index)
    this.clearError()
    this.sync()
  }

  clear = () => {
    this.stagedFiles = []
    this.clearError()
    this.sync()
  }

  // 送信される input.files を、必ず stagedFiles（一覧に見えているもの）と一致させる
  syncBeforeSubmit = (event) => {
    if (event.target === this.inputTarget.form) this.sync()
  }

  isAcceptableType(file) {
    const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`
    return this.acceptedExtensions.includes(extension)
  }

  isDuplicate(file, pending) {
    const key = this.constructor.duplicateKey(file)
    return [...this.stagedFiles, ...pending].some((f) => this.constructor.duplicateKey(f) === key)
  }

  static duplicateKey(file) {
    return `${file.name}:${file.size}`
  }

  get acceptedExtensions() {
    return this.inputTarget.accept.split(",").map((ext) => ext.trim().toLowerCase())
  }

  // stagedFiles を input.files へ組み直し、一覧を再描画する
  sync() {
    const dataTransfer = new DataTransfer()
    this.stagedFiles.forEach((file) => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files

    this.renderList()
  }

  renderList() {
    this.listTarget.innerHTML = ""
    this.stagedFiles.forEach((file, index) => {
      const node = this.itemTemplateTarget.content.cloneNode(true)
      node.querySelector('[data-attachment-item="name"]').textContent = file.name
      const removeButton = node.querySelector('[data-attachment-item="remove"]')
      removeButton.setAttribute("aria-label", `${file.name}を削除`)
      removeButton.dataset.selectedAttachmentsIndexParam = index
      this.listTarget.appendChild(node)
    })
  }

  showError(message) {
    this.errorBannerTarget.innerHTML = ""
    const node = this.errorTemplateTarget.content.cloneNode(true)
    node.querySelector('[data-message-panel-target="heading"]').textContent = message
    this.errorBannerTarget.appendChild(node)
  }

  clearError() {
    this.errorBannerTarget.innerHTML = ""
  }
}
